/**
 * Icons
 *
 * Notes to designers:
 * - We draw at 24px (auspost styleguide has this size), this matches the defaultSize here.
 * - Create a `.svg` file with the contents as shown below and edit in your editor of choice (I use inkscape)
 * - After editing the file in your editor you might need to clean some gunk (e.g. inkscape meta data)
 *
 * <svg width="24" height="24">
 *  // Paste your contents here
 * </svg>
 *
 * More Tips:
 * - If the icon doesn't have a nice aspect ratio feel free to tweek the viewBox to be exactly what you want
 * - `display:block` to prevent weird sizing issues regarding the parent's line height
 */
import * as React from 'react';
import { colors } from './styles';

/**
 * Some color defaults
 */
const defaultColor = colors.warmGrey4;
const successColor = colors.success;
const failColor = colors.red;
const currentColor = colors.current;

/**
 * Icon size defaults
 * This is the size that the icons should be created with and matches the viewBox setting
 */
const defaultSize = 24;

interface IconProps {
  color?: string;
  size?: number;
}

/**
 * Also called `success`
 * https://app.frontify.com/d/zYF6HCW0aIp5/ui-library#/components/nav-stepper
 */
export class Done extends React.Component<IconProps, {}> {
  render() {
    const color = this.props.color || successColor;
    const size = this.props.size || defaultSize;

    return (
      <svg viewBox={`0 0 ${defaultSize} ${defaultSize}`} width={size} height={size} style={{ fill: color }}>
        <path d="M12 20c4.4 0 8-3.6 8-8s-3.6-8-8-8-8 3.6-8 8 3.6 8 8 8zm0 1.5c-5.2 0-9.5-4.3-9.5-9.5S6.8 2.5 12 2.5s9.5 4.3 9.5 9.5-4.3 9.5-9.5 9.5z"></path>
        <path d="M11.1 12.9l-1.2-1.1c-.4-.3-.9-.3-1.3 0-.3.3-.4.8-.1 1.1l.1.1 1.8 1.6c.1.1.4.3.7.3.2 0 .5-.1.7-.3l3.6-4.1c.3-.3.4-.8.1-1.1l-.1-.1c-.4-.3-1-.3-1.3 0l-3 3.6z"></path>
      </svg>
    )
  }
}

/**
 * https://app.frontify.com/d/zYF6HCW0aIp5/ui-library#/components/nav-stepper
 */
export class Current extends React.Component<IconProps, {}> {
  render() {
    const color = this.props.color || currentColor;
    const size = this.props.size || defaultSize;

    return (
      <svg viewBox={`0 0 ${defaultSize} ${defaultSize}`} width={size} height={size} style={{ fill: color }}>
        <path d="M12.2 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm0 1.377a9.377 9.377 0 1 1 0-18.754 9.377 9.377 0 0 1 0 18.754zm-4-8a1.377 1.377 0 1 1 0-2.754 1.377 1.377 0 0 1 0 2.754zm4 0a1.377 1.377 0 1 1 0-2.754 1.377 1.377 0 0 1 0 2.754zm4 0a1.377 1.377 0 1 1 0-2.754 1.377 1.377 0 0 1 0 2.754z"></path>
      </svg>
    )
  }
}

/**
 * Pending
 */
export class Pending extends React.Component<IconProps, {}> {
  render() {
    const color = this.props.color || defaultColor;
    const size = this.props.size || defaultSize;

    return (
      <svg viewBox={`0 0 ${defaultSize} ${defaultSize}`} width={size} height={size} style={{ fill: color }}>
        <path d="M12 16.1c1.8 0 3.3-1.4 3.3-3.2 0-1.8-1.5-3.2-3.3-3.2s-3.3 1.4-3.3 3.2c0 1.7 1.5 3.2 3.3 3.2zm0 1.7c-2.8 0-5-2.2-5-4.9S9.2 8 12 8s5 2.2 5 4.9-2.2 4.9-5 4.9z"></path>
      </svg>
    )
  }
}

/**
 * Fail
 */
export class Fail extends React.Component<IconProps, {}> {
  render() {
    const color = this.props.color || failColor;
    const size = this.props.size || defaultSize;

    return (
      <svg viewBox={`0 0 ${defaultSize} ${defaultSize}`} width={size} height={size} style={{ fill: color }}>
        <path d="M12 20c4.4 0 8-3.6 8-8s-3.6-8-8-8-8 3.6-8 8 3.6 8 8 8zm0 1.5c-5.2 0-9.5-4.3-9.5-9.5S6.8 2.5 12 2.5s9.5 4.3 9.5 9.5-4.3 9.5-9.5 9.5z"></path>
        <path d="M 15.681641,7.4589844 A 0.90008998,0.90008998 0 0 0 15.0625,7.7285156 L 7.953125,14.775391 a 0.90008998,0.90008998 0 1 0 1.265625,1.277343 l 7.109375,-7.0468746 a 0.90008998,0.90008998 0 0 0 -0.646484,-1.546875 z"></path>
        <path d="M 8.5703125,7.4882812 A 0.90008998,0.90008998 0 0 0 7.9277344,9.0273438 l 7.1093746,7.2656252 a 0.90008998,0.90008998 0 1 0 1.285157,-1.257813 L 9.2128906,7.7695312 a 0.90008998,0.90008998 0 0 0 -0.6425781,-0.28125 z"></path>
      </svg>
    )
  }
}

export class File extends React.Component<IconProps, {}>{
  render() {
    const color = this.props.color || defaultColor;
    const size = this.props.size || defaultSize;

    return (
      <svg viewBox={`0 0 17 20`} width={size} height={size} style={{ fill: color }}>
        <g id="Artboard" fill="#807370">
          <path d="M11.9615385,13.375 L5.03846154,13.375 C4.72,13.375 4.46153846,13.6272812 4.46153846,13.9375 C4.46153846,14.2482813 4.72,14.5 5.03846154,14.5 L11.9615385,14.5 C12.28,14.5 12.5384615,14.2482813 12.5384615,13.9375 C12.5384615,13.6272812 12.28,13.375 11.9615385,13.375 L11.9615385,13.375 Z M4.46153846,10.5630625 C4.46153846,10.8732813 4.72,11.1255625 5.03846154,11.1255625 L11.9615385,11.1255625 C12.28,11.1255625 12.5384615,10.8732813 12.5384615,10.5630625 C12.5384615,10.2517188 12.28,10 11.9615385,10 L5.03846154,10 C4.72,10 4.46153846,10.2517188 4.46153846,10.5630625 L4.46153846,10.5630625 Z M14.8461538,17.3125 C14.8461538,17.6232813 14.5876923,17.875 14.2692308,17.875 L2.73076923,17.875 C2.41230769,17.875 2.15384615,17.6232813 2.15384615,17.3125 L2.15384615,2.68778125 C2.15384615,2.377 2.41230769,2.1255625 2.73076923,2.1255625 L10.2307692,2.1255625 L10.2307692,5.5 L10.2307692,6.6255625 L11.3846154,6.6255625 L14.8461538,6.6255625 L14.8461538,17.3125 Z M11.3846154,2.3865625 L14.5784615,5.5 L11.3846154,5.5 L11.3846154,2.3865625 Z M15.9982692,5.5 L11.3846154,1.0016875 L11.3846154,1 L1.57692308,1 C1.25817308,1 1,1.252 1,1.5625 L1,18.4377813 C1,18.7482813 1.25817308,19 1.57692308,19 L15.4230769,19 C15.7415385,19 16,18.7482813 16,18.4377813 L16,5.5 L15.9982692,5.5 Z" id="Page-1"></path>
        </g>
      </svg>
    )
  }
}

export class Mail extends React.Component<{}, {}>{
  render() {
    return (
      <svg width="24px" height="24px" viewBox="0 0 25 25">
        <g id="Page-1" stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
          <g id="icn-mail" fill="#807370">
            <g id="Fill-22-+-Fill-23" transform="translate(3.000000, 6.000000)">
              <path d="M11.541,7.064 C11.006,7.439 10.219,7.662 9.433,7.662 L9.418,7.662 C8.629,7.662 7.842,7.437 7.307,7.062 L0,2 L0,11.474 C0,11.885 0.334,12.22 0.746,12.22 L17.869,12.22 C18.277,12.22 18.613,11.885 18.613,11.474 L18.613,2.166 L11.541,7.064" id="Fill-22" />
              <path d="M8.086,6.549 C8.396,6.768 8.916,6.905 9.422,6.905 L9.432,6.905 C9.934,6.905 10.457,6.766 10.766,6.551 L18.613,1.111 L18.613,0.746 C18.613,0.334 18.277,0 17.869,0 L0.746,0 C0.334,0 0,0.334 0,0.746 L0,0.945 L8.086,6.549" id="Fill-23" />
            </g>
          </g>
        </g>
      </svg>
    );
  }
}

/**
 * Search icon
 * Source: auspost homepage
 */
export class Search extends React.Component<{}, {}> {
  render() {
    return (
      <svg width={24} height={24} viewBox="0 0 21 19">
        <path d="M9.086 15.74c-3.78 0-6.842-3.02-6.842-6.752 0-3.732 3.063-6.758 6.842-6.758 3.78 0 6.842 3.026 6.842 6.758 0 3.73-3.063 6.75-6.842 6.75zm5.99-1.405a7.88 7.88 0 0 0 1.58-8.16C15.507 3.15 12.55 1 9.087 1 4.62 1 1 4.575 1 8.988c0 4.406 3.62 7.98 8.086 7.98 1.93 0 3.7-.668 5.09-1.783l4.98 4.92c.244.24.638.24.88 0a.607.607 0 0 0 0-.87l-4.96-4.9z" fill="#51534A" stroke="#51534A" fillRule="evenodd" />
      </svg>

    );
  }
}

/**
 * AP logo
 * To Update:
 * - Visit https://auspost.com.au/
 * - Inspect the logo in dev tools and paste in the SVG contents
 */
export class APLogo extends React.Component<{ size?: number }, {}>{
  render() {
    const size = this.props.size || 40;
    return (
      <svg width={size} height={size} viewBox="0 0 54 53" style={{ display: 'block' }}><path d="M54 50.922C54 52.068 53.052 53 51.903 53H2.097C.947 53 0 52.068 0 50.922V2.078C0 .932.948 0 2.097 0h49.806C53 0 53.947.932 54 2.078v48.844z" fill="#DC1928"></path><path d="M9.594 26.448c0 7.378 4.666 13.718 11.272 16.212V10.236C14.26 12.73 9.594 19.07 9.594 26.448M27.052 9.145c-.47 0-.943 0-1.415.052v1.195h.052c6.658.052 12.005 5.092 11.953 11.224-.053 6.13-5.453 11.067-12.06 11.067V43.7c.473.05.945.05 1.417.05 9.647 0 17.458-7.74 17.458-17.302.053-9.56-7.81-17.303-17.406-17.303" fill="#FFF"></path></svg>
    );
  }
}
